import React, { useState, useMemo, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { SprayCan, Settings2, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import AppHeader from "@/components/AppHeader";
import AdminSettings from "@/components/AdminSettings";
import { useAuth } from "@/context/AuthContext";
import SizeSelector from "@/components/quote/SizeSelector";
import PanelsSection from "@/components/quote/PanelsSection";
import { SpecialEffectSection, ServicesSection, PartsSection } from "@/components/quote/AddonSections";
import FullRespraySection from "@/components/quote/FullRespraySection";
import QuoteSummary from "@/components/quote/QuoteSummary";
import CustomerFields from "@/components/quote/CustomerFields";
import LinkedCustomerBanner from "@/components/quote/LinkedCustomerBanner";
import PresetBar, { buildPresets } from "@/components/quote/PresetBar";
import { useSettings } from "@/hooks/useSettings";
import { useLinkedCustomer } from "@/hooks/useLinkedCustomer";
import { useQuoteBuilder } from "@/hooks/useQuoteBuilder";
import { calculateQuote, isEmptyQuote } from "@/lib/pricing";
import { quoteDocText } from "@/lib/quoteText";
import { docFromLive, shopFromSettings } from "@/lib/quoteDoc";
import { useQuotePrinter } from "@/hooks/useQuotePrinter";
import { copyText } from "@/lib/clipboard";
import { BACKEND_URL } from "@/lib/backendUrl";

const API = `${BACKEND_URL}/api`;

// Older quotes (saved before builder_state existed) are rebuilt from their line items
const stateFromSavedQuote = (qt) => {
  const selections = {};
  (qt.line_items || []).forEach((it) => {
    const qty = Number(it.qty || 1);
    selections[it.key] = { selected: true, qty, sprayMode: it.sprayMode || "full", repair: it.repairTier || "none", repairPrice: it.repair > 0 ? it.repair / qty : null, touchupPrice: it.sprayMode === "touchup" ? it.respray / qty : null, resprayPrice: it.sprayMode !== "touchup" && it.respray > 0 ? it.respray / qty : null, special: it.special > 0 };
  });
  const services = {}; const customServices = [];
  (qt.services || []).filter((sv) => sv.id !== "full_respray").forEach((sv) => (sv.custom || String(sv.id).startsWith("custom_svc") ? customServices.push({ id: sv.id, en: sv.en, price: sv.price }) : (services[sv.id] = String(sv.price ?? ""))));
  const parts = (qt.parts || []).map((p) => ({ id: p.id, name: p.name, price: p.price, ri: p.ri || 0 }));
  const d = qt.discount || {};
  return { selections, customPanels: [], services, customServices, parts, discount: { mode: d.mode || "percent", value: d.value ? String(d.value) : "" } };
};

const quotePayload = ({ quote, size, fields, linked, linkedVehicle, editing, scheduled }) => ({
  customer_id: linked?.id || editing?.customer_id || null,
  vehicle_id: linkedVehicle?.id || editing?.vehicle_id || null,
  scheduled_date: scheduled || null,
  customer_name: fields.customer,
  customer_phone: fields.phone,
  vehicle: fields.vehicle,
  plate: fields.plate.toUpperCase(),
  paint_code: fields.paintCode.toUpperCase(),
  size,
  line_items: quote.lineItems,
  services: quote.serviceItems,
  parts: quote.partItems,
  breakdown: { respray: quote.respraySubtotal, repair: quote.repairSubtotal, special: quote.specialSubtotal, services: quote.servicesSubtotal, parts: quote.partsSubtotal, tax_rate: quote.taxRate, effect_mode: quote.mode },
  subtotal: quote.subtotal,
  tax: quote.tax,
  total: quote.total,
  discount: { mode: quote.discountMode, value: quote.discountValue, amount: quote.discountAmount },
});

export default function Calculator() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [adminOpen, setAdminOpen] = useState(false);

  const { settings, effectiveSettings, effectMode, sym, refresh, saveSettings, resetSettings, changeEffectMode } = useSettings();
  const { isOwner } = useAuth();
  const editId = params.get("edit");
  const { linked, linkedVehicle, fields, setField, setFields, pickVehicle, clearFields } = useLinkedCustomer(params.get("customer"), params.get("vehicle"), { keepFields: !!editId });
  const scheduled = params.get("scheduled"); // from the calendar quick-add → quote lands on that day once approved

  // Walk-in prefill from the calendar quick-add (?name=&phone=&plate=&make=) — linked customers load their own record
  useEffect(() => {
    if (editId || params.get("customer")) return;
    const pre = { customer: params.get("name") || "", phone: params.get("phone") || "", plate: params.get("plate") || "", vehicle: params.get("make") || "" };
    if (Object.values(pre).some(Boolean)) setFields((f) => ({ ...f, ...pre }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!linked) return;
    const plate = (params.get("plate") || "").toUpperCase(), make = params.get("make");
    if (plate && plate !== (linkedVehicle?.plate || "").toUpperCase()) { pickVehicle(null); setFields((f) => ({ ...f, plate, vehicle: make || "" })); }
    else if (make && !fields.vehicle) setField("vehicle", make);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linked]);
  const [saved, setSaved] = useState(null); // { id, quote_no } once this estimate exists in History
  const [saving, setSaving] = useState(false);
  const clientRef = useRef(crypto.randomUUID());
  const savingRef = useRef(null);
  const builder = useQuoteBuilder();
  const { size, setSize, selections, customPanels, services, customServices, parts, discount, setDiscount, fullRespray } = builder;

  const quote = useMemo(
    () => calculateQuote({ settings: effectiveSettings, size, selections, customPanels, services, customServices, parts, discount, fullRespray }),
    [effectiveSettings, size, selections, customPanels, services, customServices, parts, discount, fullRespray]
  );
  const presets = useMemo(() => (settings ? buildPresets(settings) : []), [settings]);
  const { exportPdf, sharePdf, print, portal } = useQuotePrinter();
  const liveDoc = (ref) => docFromLive({ quote, size, fields, shop: shopFromSettings(settings), ref: ref || saved?.quote_no });
  const quoteText = (savedDoc) => quoteDocText(liveDoc(savedDoc?.quote_no));

  const printQuote = () => (isEmptyQuote(quote) ? toast.error("Nothing to print yet") : print(liveDoc()));
  const pdfQuote = () => (isEmptyQuote(quote) ? toast.error("Nothing to export yet") : exportPdf(liveDoc()));
  const shareQuotePdf = async () => {
    if (isEmptyQuote(quote)) return toast.error("Nothing to share yet");
    const data = await ensureSaved();
    const doc = liveDoc(data?.quote_no);
    sharePdf(doc, { text: quoteDocText(doc), phone: fields.phone, countryCode: settings.phone_country_code });
  };

  // Edit draft: restore the saved builder state
  useEffect(() => {
    if (!editId || !settings) return;
    axios.get(`${API}/quotes/${editId}`).then(({ data }) => {
      builder.loadState({ ...(data.builder_state || stateFromSavedQuote(data)), size: data.size || "medium" });
      setFields({ customer: data.customer_name || "", phone: data.customer_phone || "", vehicle: data.vehicle || "", plate: data.plate || "", paintCode: data.paint_code || "" });
      setSaved({ id: data.id, quote_no: data.quote_no, customer_id: data.customer_id, vehicle_id: data.vehicle_id });
      toast.success(`Editing ${data.quote_no}`, { description: "Changes will update this draft · 编辑草稿" });
    }).catch(() => toast.error("Draft not found"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId, !!settings]);

  const resetAll = () => {
    builder.clear();
    setSaved(null);
    clientRef.current = crypto.randomUUID();
    if (!linked) clearFields();
    toast.success("Cleared", { description: "Estimate reset · 已重置" });
  };

  const applyPreset = (preset) => {
    builder.applyPreset(preset);
    if (!linked) setField("vehicle", preset.vehicle || "");
    toast.success("Preset loaded", { description: preset.name });
  };

  useEffect(() => { if (isOwner && new URLSearchParams(window.location.search).get("settings") === "1") openAdmin(); }, [isOwner]); // eslint-disable-line react-hooks/exhaustive-deps
  const openAdmin = async () => {
    await refresh();
    setAdminOpen(true);
  };

  const onSaveSettings = async (draft) => {
    try {
      await saveSettings(draft);
      setAdminOpen(false);
    } catch (err) { console.warn("Settings not saved (dialog stays open):", err?.response?.status || err?.message); }
  };

  const copyQuote = async () => {
    if (isEmptyQuote(quote)) return toast.error("Nothing to quote yet");
    const ok = await copyText(quoteText());
    if (ok) toast.success("Copied", { description: "Quote copied for WhatsApp" });
    else toast.error("Copy blocked", { description: "Select the quote text and copy manually" });
  };

  // Save (or update) — one in-flight request at a time; the server also de-duplicates by client_ref
  const saveQuote = async ({ silent = false } = {}) => {
    if (isEmptyQuote(quote)) { if (!silent) toast.error("Nothing to save yet"); return null; }
    if (savingRef.current) return savingRef.current;
    setSaving(true);
    const payload = { ...quotePayload({ quote, size, fields, linked, linkedVehicle, editing: saved, scheduled }), builder_state: builder.state(), client_ref: clientRef.current };
    savingRef.current = (saved
      ? axios.put(`${API}/quotes/${saved.id}`, payload)
      : axios.post(`${API}/quotes`, payload)
    ).then(({ data }) => {
      setSaved({ id: data.id, quote_no: data.quote_no, customer_id: data.customer_id, vehicle_id: data.vehicle_id });
      const historyUrl = linked ? `/quotes?customer=${linked.id}` : "/quotes";
      if (!silent) {
        toast.success(`Quote ${data.quote_no} ${saved ? "updated" : "saved"}`, { description: "报价已存档 · opening Activity log" });
        navigate(historyUrl); // saved quotes continue in the Activity log (approve → invoice)
      }
      return data;
    }).catch((e) => {
      toast.error("Could not save quote", { description: e?.response?.data?.detail || "Check your connection and try again" });
      throw e;
    }).finally(() => { setSaving(false); savingRef.current = null; });
    return savingRef.current;
  };

  // Sharing to WhatsApp always leaves a draft in History
  const ensureSaved = async () => {
    const data = await saveQuote({ silent: true });
    if (data && !saved) toast.success(`Saved as draft ${data.quote_no}`, { description: "自动存为草稿" });
    return data;
  };

  if (!settings) {
    return (
      <div className="min-h-screen workshop-bg flex items-center justify-center text-slate-400">
        <SprayCan className="w-6 h-6 animate-pulse mr-2 text-amber-400" /> Loading workshop…
      </div>
    );
  }

  return (
    <div className="min-h-screen workshop-bg text-slate-100">
      <AppHeader right={
        <>
          <Button variant="outline" onClick={resetAll} data-testid="reset-estimate-btn" className="border-slate-700 bg-slate-900/60 hover:bg-slate-800 text-slate-300 hidden sm:flex">
            <RotateCcw className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Reset</span>
          </Button>
          {isOwner && <Button onClick={openAdmin} data-testid="admin-settings-modal-btn" className="bg-slate-800 hover:bg-slate-700 text-amber-300 border border-amber-500/30">
            <Settings2 className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Prices</span>
          </Button>}
        </>
      } />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        <main className="lg:col-span-8 space-y-6">
          <LinkedCustomerBanner linked={linked} linkedVehicle={linkedVehicle} onPick={pickVehicle} onUnlink={() => navigate("/quote")} />
          <PresetBar presets={presets} onApply={applyPreset} />
          <SizeSelector size={size} onChange={setSize} settings={settings} sym={sym} />
          <PanelsSection settings={settings} sym={sym} size={size} builder={builder} collapsed={fullRespray.scope !== "off"} />
          <SpecialEffectSection effectMode={effectMode} onChange={changeEffectMode} settings={settings} sym={sym} />
          <ServicesSection settings={settings} services={services} onToggle={builder.toggleService} onPrice={builder.setServicePrice} sym={sym}
            customServices={customServices} onAddCustom={builder.addCustomService} onRemoveCustom={builder.removeCustomService} />
          <PartsSection parts={parts} sym={sym} onAdd={builder.addPart} onUpdate={builder.updatePart} onRemove={builder.removePart} />
          <FullRespraySection settings={settings} sym={sym} fullRespray={fullRespray} onChange={builder.setFullRespray} />
        </main>

        <aside className="lg:col-span-4 lg:sticky lg:top-24 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto lg:pr-1 space-y-4" data-testid="quote-summary-aside">
          <QuoteSummary quote={quote} sym={sym} size={size} discount={discount} setDiscount={setDiscount} />
          <CustomerFields fields={fields} setField={setField} onCopy={copyQuote} onPrint={printQuote} onPdf={pdfQuote} onSave={() => saveQuote()} saving={saving} savedNo={saved?.quote_no} onBeforeShare={ensureSaved}
            getText={(savedDoc) => (isEmptyQuote(quote) ? "" : quoteText(savedDoc))} countryCode={settings.phone_country_code} onSharePdf={shareQuotePdf} />
        </aside>
      </div>

      {portal}

      <AdminSettings open={adminOpen} onOpenChange={setAdminOpen} settings={settings} onSave={onSaveSettings} onReset={resetSettings} />
    </div>
  );
}
